<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import type { Emits, Props } from './type'

defineOptions({
  name: 'SearchHorizontalStrip',
})

const props = withDefaults(defineProps<Props>(), {
  itemCount: 0,
  ariaLabel: undefined,
  showMoreAction: true,
})
const emit = defineEmits<Emits>()

const rootElement = shallowRef<HTMLElement | null>(null)
const scrollerElement = shallowRef<HTMLElement | null>(null)
const showChrome = shallowRef(false)
const canScroll = shallowRef(false)
const canScrollLeft = shallowRef(false)
const canScrollRight = shallowRef(false)
let resizeObserver: ResizeObserver | null = null

function syncScrollState() {
  const element = scrollerElement.value

  if (!element) {
    canScroll.value = false
    canScrollLeft.value = false
    canScrollRight.value = false
    return
  }

  const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth)
  const scrollLeft = Math.max(0, element.scrollLeft)

  canScroll.value = maxScrollLeft > 2
  canScrollLeft.value = scrollLeft > 2
  canScrollRight.value = scrollLeft < maxScrollLeft - 2
}

function scrollByDirection(direction: -1 | 1) {
  scrollerElement.value?.scrollBy({
    left: direction * 460,
    behavior: 'smooth',
  })

  requestAnimationFrame(syncScrollState)
}

function setupResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null

  syncScrollState()

  if (!rootElement.value || !scrollerElement.value || typeof ResizeObserver === 'undefined') {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    syncScrollState()
  })
  resizeObserver.observe(rootElement.value)
  resizeObserver.observe(scrollerElement.value)
}

onMounted(() => {
  setupResizeObserver()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () => props.itemCount,
  () => {
    requestAnimationFrame(setupResizeObserver)
  },
)
</script>

<template>
  <section
    ref="rootElement"
    class="search-strip"
    :aria-label="ariaLabel ?? title"
    @pointerenter="showChrome = true"
    @pointerleave="showChrome = false"
    @focusin="showChrome = true"
    @focusout="showChrome = false"
  >
    <div class="search-strip__head">
      <button
        v-if="showMoreAction"
        type="button"
        class="search-strip__title-button"
        @click="emit('more')"
      >
        <span>{{ title }}</span>
        <ChevronRight :size="16" aria-hidden="true" />
      </button>
      <h2 v-else class="search-strip__title">{{ title }}</h2>
    </div>

    <div v-if="canScroll" class="search-strip__fade is-left" aria-hidden="true" />
    <div v-if="canScroll" class="search-strip__fade is-right" aria-hidden="true" />

    <button
      v-if="canScroll"
      type="button"
      class="search-strip__nav is-left"
      :class="{ 'is-visible': showChrome, 'is-disabled': !canScrollLeft }"
      :disabled="!canScrollLeft"
      :aria-label="`向左查看${title}`"
      @click="scrollByDirection(-1)"
    >
      <ChevronLeft :size="18" stroke-width="2.4" />
    </button>

    <button
      v-if="canScroll"
      type="button"
      class="search-strip__nav is-right"
      :class="{ 'is-visible': showChrome, 'is-disabled': !canScrollRight }"
      :disabled="!canScrollRight"
      :aria-label="`向右查看${title}`"
      @click="scrollByDirection(1)"
    >
      <ChevronRight :size="18" stroke-width="2.4" />
    </button>

    <div ref="scrollerElement" class="search-strip__scroller" @scroll="syncScrollState">
      <slot />
    </div>
  </section>
</template>

<style scoped>
.search-strip {
  position: relative;
  width: 100%;
  padding: 0;
  --search-strip-mask-base: var(--app-bg);
  --search-strip-nav-bg: var(--app-surface-raised);
  --search-strip-nav-fg: var(--app-text-soft);
  --search-strip-nav-shadow: var(--app-shadow-floating);
}

.search-strip__head {
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 0 12px;
}

.search-strip__title,
.search-strip__title-button {
  margin: 0;
  color: var(--app-text-main);
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.search-strip__title-button {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  cursor: pointer;
  outline: none;
  transition: color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-strip__title-button:hover,
.search-strip__title-button:focus-visible {
  color: var(--app-text-soft);
}

.search-strip__scroller {
  display: flex;
  min-width: 0;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 12px 0;
  scroll-behavior: smooth;
  scrollbar-width: none;
}

.search-strip__scroller::-webkit-scrollbar {
  display: none;
}

.search-strip__fade {
  position: absolute;
  top: 30px;
  z-index: 1;
  width: 54px;
  height: 178px;
  pointer-events: none;
}

.search-strip__fade.is-left {
  left: 0;
  background: linear-gradient(90deg, var(--search-strip-mask-base) 0%, rgb(255 255 255 / 0) 100%);
}

.search-strip__fade.is-right {
  right: 0;
  background: linear-gradient(270deg, var(--search-strip-mask-base) 0%, rgb(255 255 255 / 0) 100%);
}

.search-strip__nav {
  position: absolute;
  top: 104px;
  z-index: 2;
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: var(--search-strip-nav-bg);
  box-shadow: var(--search-strip-nav-shadow);
  color: var(--search-strip-nav-fg);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.search-strip__nav.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.search-strip__nav.is-disabled {
  opacity: 0;
  pointer-events: none;
}

.search-strip__nav.is-left {
  left: 6px;
}

.search-strip__nav.is-right {
  right: 6px;
}

@media (max-width: 640px) {
  .search-strip__head,
  .search-strip__scroller {
    padding-inline: 8px;
  }

  .search-strip__scroller {
    gap: 8px;
  }

  .search-strip__fade,
  .search-strip__nav {
    display: none;
  }
}
</style>
